/**
 * `/link` — where a player finishes binding their Discord account.
 *
 * THE OTHER END OF A URL THE GAME SERVER PRINTS. `/link` in game mints a code, pushes it to the
 * sidecar and tells the player to open this page; the sidecar builds that URL from
 * `PUBLIC_EDITOR_URL`, so this route has to exist under exactly this path or a player clicking a
 * freshly minted link sees "not found" — indistinguishable from a broken deployment. (That is the
 * same trap `/editor/:code` documents, and it is why this page is part of the same change as the
 * command.)
 *
 * WHAT IT SENDS: the code, and nothing else. Not the Discord account (the sidecar reads that off
 * the `sub` of the token it verifies) and not the player (the sidecar reads that off its own record
 * of the game server's push). Both absences are the security model rather than an economy of
 * fields, and the sidecar refuses an unknown field rather than ignoring one — so a well-meaning
 * future addition here fails loudly.
 *
 * WHAT IT MUST NOT SAY: "linked". A 202 means an INTENT IS FILED. The mod's drain claims it on its
 * next tick, checks the code against a book it keeps in its own memory, and only then writes the
 * binding — or refuses with a reason. So the success wording is "sent to the server", with the
 * one-tick latency named, because a page that claimed success and then silently didn't happen is
 * the worst available outcome for a feature whose whole point is that the game server decides.
 *
 * NO TIER IS REQUIRED, AND THE PAGE SAYS SO BY NOT CHECKING ONE. Every other write surface in this
 * SPA gates on `admin`; this one gates on being signed in at all, because it is a person binding
 * their own account.
 *
 * AND THE PEOPLE IT IS FOR CAN NOW GET HERE, which they could not when this page was written. Back
 * then the Worker minted a token only for a staff guild role, so the one screen whose whole purpose
 * is "type the code `/link` gave you" was reachable only by somebody who did not need it — the
 * advice on the refusal screen named a remedy the system made impossible. A guild member with no
 * staff role and no binding now gets an `unlinked` session (see `worker/src/index.ts`), `/me` prints
 * the instructions, and its call to action lands here. That tier can reach exactly one route and it
 * is the one this page posts to; it reads nothing anywhere.
 */
import { useState } from 'react';
import { Link } from 'react-router';

import { LINK_CODE_LENGTH, normaliseLinkCode } from '@dimensionscore/shared';

import type { LinkOutcome } from '../api/client';
import { useSession } from '../auth/SessionProvider';
import { SignInPanel } from '../components/SessionBar';

/** What the page is currently showing. `sending` disables the button; nothing else does. */
type State = { readonly phase: 'idle' | 'sending' } | { readonly phase: 'done'; readonly outcome: LinkOutcome };

/**
 * Turns a refusal into a sentence for a player rather than for an operator.
 *
 * Keyed on the STABLE CODE, never on the `detail` prose: the sidecar's detail is written for
 * whoever is reading a log, and some of it names environment variables. Where a code has no player
 * meaning the status is shown, because "something went wrong" with no number in it is the one
 * message nobody can act on or report.
 */
export function explainRefusal(outcome: Extract<LinkOutcome, { filed: false }>): string {
  switch (outcome.rejected.error) {
    case 'invalid_code':
      return `That is not a link code. It is ${LINK_CODE_LENGTH} characters, and /link in game shows it to you.`;
    case 'unknown_code':
      return 'This server is not waiting for that code. It may have been used already, or it may have expired — run /link again in game for a fresh one.';
    case 'unauthorised':
      return 'Your sign-in has expired. Sign in with Discord again and re-enter the code.';
    case 'link_not_configured':
      return 'This server has not finished setting up account linking. Ask an operator.';
    // NO CASE FOR `too_many_pending`, DELIBERATELY. It is a LinkErrorCode, so the union offers it
    // here — but only `POST /v1/link` raises it (the store is full of codes the GAME SERVER pushed,
    // web/backend/src/link.ts), and that route is game-server-authenticated and not proxied to the
    // browser at all. This page posts to `/v1/link/claim`, which cannot answer it. Handling it would
    // put a sentence on screen implying the CLAIM path has a pending cap, which it does not; if it
    // ever did arrive it is a bug, and the default's status-carrying wording is the right answer to
    // a bug. Do not re-add it without a route that can produce it.
    default:
      // Everything else, including the edge's own refusals (429 from the rate limiter, 502 when the
      // sidecar is down) which are NOT LinkErrorCodes and must not be dressed up as one. 429 is
      // told apart here because "you are going too fast" is actionable and "something went wrong"
      // is not.
      if (outcome.status === 429) return 'That is a lot of attempts. Wait a minute and try again.';
      return `The server could not accept that right now (${outcome.status}). Try again, and tell an operator if it keeps happening.`;
  }
}

export function LinkPage(): React.JSX.Element {
  const { session, client } = useSession();
  const [typed, setTyped] = useState('');
  const [state, setState] = useState<State>({ phase: 'idle' });

  if (session === null) {
    return (
      <section className="dc-card" data-testid="page-link">
        <h1>Link your Discord account</h1>
        <p className="dc-dim">
          Run <code>/link</code> in game to get a code, then sign in here with the Discord account you want to link.
        </p>
        <SignInPanel />
      </section>
    );
  }

  // Normalised as the player types, so the button's enabled state and what is sent are the same
  // judgement. `null` means "not a code yet", which is also the empty-field state — hence the
  // separate `typed.length` check for whether to show the hint at all.
  const normalised = normaliseLinkCode(typed);
  const ready = normalised !== null && state.phase !== 'sending';

  async function submit(): Promise<void> {
    if (normalised === null) return;
    setState({ phase: 'sending' });
    try {
      setState({ phase: 'done', outcome: await client.claimLink(normalised) });
    } catch (cause) {
      // A request that produced no HTTP answer at all — offline, DNS, a blocked origin. Reported
      // as itself rather than as a server refusal, because those are different things to fix.
      setState({
        phase: 'done',
        outcome: { filed: false, status: 0, rejected: { error: 'edge_refused', detail: cause instanceof Error ? cause.message : String(cause) } },
      });
    }
  }

  return (
    <section className="dc-card" data-testid="page-link">
      <h1>Link your Discord account</h1>
      <p className="dc-dim">
        Signed in as <strong>{session.claims.name}</strong>. Run <code>/link</code> in game, then enter the code it shows you.
      </p>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label htmlFor="dc-link-code">Link code</label>{' '}
        <input
          id="dc-link-code"
          data-testid="link-code-input"
          value={typed}
          autoComplete="off"
          spellCheck={false}
          // A generous cap rather than exactly the code length: the field accepts the spaces and
          // hyphens a person adds unprompted, which normaliseLinkCode then strips.
          maxLength={LINK_CODE_LENGTH * 3}
          onChange={(event) => setTyped(event.target.value)}
          placeholder={'X'.repeat(LINK_CODE_LENGTH)}
        />{' '}
        <button type="submit" data-testid="link-submit" disabled={!ready}>
          {state.phase === 'sending' ? 'Sending…' : 'Link my account'}
        </button>
      </form>

      {typed.length > 0 && normalised === null ? (
        <p className="dc-dim" data-testid="link-shape-hint">
          A link code is {LINK_CODE_LENGTH} characters. Letters and digits only — and note that <code>O</code>, <code>I</code>, <code>L</code> and{' '}
          <code>U</code> never appear in one, so if you read one of those it is a <code>0</code> or a <code>1</code>.
        </p>
      ) : null}

      {state.phase === 'done' && state.outcome.filed ? (
        <p data-testid="link-filed">
          {/*
            "Sent", not "linked". The mod has not seen this yet: it claims the intent on its own
            tick (a second or two) and can still refuse it — the Discord account may already belong
            to another player, or the code may have lapsed between here and there. Claiming success
            now and being wrong later is the failure this wording exists to avoid.
          */}
          Sent to the server. It applies the binding on its next pass — a second or two — and will refuse it if that Discord account already
          belongs to another player. Run <code>/link</code> again if nothing changes.
          {/*
            THE STEP THAT IS EASY TO LEAVE OUT AND LOOKS LIKE A BUG WITHOUT IT. The session this
            request was made with was minted BEFORE the binding existed, so it still carries no
            player and `/me` still shows the instructions. Nothing refreshes it: the tier is stamped
            into a signed token at login, so the only way to hold a `player` one is to sign in again.
            Saying so here is the difference between "it worked" and "it did nothing".
          */}{' '}
          Then sign out and sign in again: this session was minted before the binding existed, so it still carries no Minecraft account and{' '}
          <Link to="/me">your own page</Link> will stay empty until you do.
        </p>
      ) : null}

      {state.phase === 'done' && !state.outcome.filed ? (
        <p data-testid="link-refused" className="dc-error">
          {explainRefusal(state.outcome)}
        </p>
      ) : null}
    </section>
  );
}
